import React, {useCallback, useState, useMemo} from 'react';
import {View, FlatList, ScrollView, StyleSheet, RefreshControl, TouchableOpacity} from 'react-native';
import {
  Text, Card, Chip, FAB, ActivityIndicator, Searchbar, Snackbar,
  IconButton, Dialog, Portal, Button,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useFocusEffect} from '@react-navigation/native';
import {userApi} from '../../services/api/userApi';
import {orgUnitApi} from '../../services/api/orgUnitApi';
import {roleApi} from '../../services/api/roleApi';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const UserListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const [users, setUsers] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [snackbar, setSnackbar] = useState('');

  // Filter state
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterOrgUnitId, setFilterOrgUnitId] = useState(null);
  const [filterRoleId, setFilterRoleId] = useState(null);
  const [filterStatus, setFilterStatus] = useState(true); // true=active default
  const [filterHasWorker, setFilterHasWorker] = useState(null);

  // Deactivate dialog
  const [actionUser, setActionUser] = useState(null);
  const [deactivateVisible, setDeactivateVisible] = useState(false);

  const hasFilters = filterOrgUnitId || filterRoleId || filterStatus !== null || filterHasWorker !== null;

  const fetchLookups = useCallback(async () => {
    try {
      const [ouRes, roleRes] = await Promise.all([
        orgUnitApi.listSimple(),
        roleApi.list(),
      ]);
      setOrgUnits(ouRes.data.data || []);
      setRoles(roleRes.data.data || []);
    } catch (e) {
      // silent
    }
  }, []);

  const fetchUsers = useCallback(async (query) => {
    try {
      const params = {page: 0, size: 100};
      if (query) params.q = query;
      if (filterOrgUnitId) params.orgUnitId = filterOrgUnitId;
      if (filterRoleId) params.roleId = filterRoleId;
      if (filterStatus !== null) params.isActive = filterStatus;
      if (filterHasWorker !== null) params.hasWorker = filterHasWorker;

      const res = await userApi.search(params);
      setUsers(res.data.data.content || []);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [filterOrgUnitId, filterRoleId, filterStatus, filterHasWorker, t]);

  useFocusEffect(
    useCallback(() => {
      setLoading(true);
      Promise.all([fetchUsers(searchQuery), fetchLookups()])
        .finally(() => setLoading(false));
    }, [fetchUsers, fetchLookups, searchQuery])
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchUsers(searchQuery);
    setRefreshing(false);
  }, [fetchUsers, searchQuery]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const handleSearchSubmit = useCallback(() => {
    setLoading(true);
    fetchUsers(searchQuery).finally(() => setLoading(false));
  }, [fetchUsers, searchQuery]);

  const clearFilters = useCallback(() => {
    setFilterOrgUnitId(null);
    setFilterRoleId(null);
    setFilterStatus(null);
    setFilterHasWorker(null);
    setFilterVisible(false);
  }, []);

  const handleDeactivate = useCallback(async () => {
    if (!actionUser) return;
    setDeactivateVisible(false);
    try {
      await userApi.delete(actionUser.id);
      await fetchUsers(searchQuery);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
    setActionUser(null);
  }, [actionUser, fetchUsers, searchQuery, t]);

  const handleReactivate = useCallback(async (u) => {
    try {
      await userApi.reactivate(u.id);
      await fetchUsers(searchQuery);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [fetchUsers, searchQuery, t]);

  const orgUnitMap = useMemo(() => {
    const map = {};
    orgUnits.forEach(o => { map[o.id] = o.name; });
    return map;
  }, [orgUnits]);

  const roleMap = useMemo(() => {
    const map = {};
    roles.forEach(r => { map[r.id] = r.name; });
    return map;
  }, [roles]);

  const renderUser = useCallback(({item}) => {
    const primaryAssignment = item.orgRoleAssignments?.find(a => a.isPrimary);
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={() => navigation.navigate('UserDetail', {userId: item.id})}>
        <Card style={[styles.card, !item.isActive && styles.cardInactive]}>
          <Card.Content style={styles.cardContent}>
            <View style={styles.cardHeader}>
              <View style={styles.avatar}>
                <Text style={styles.avatarText}>{(item.fullName || '?').charAt(0).toUpperCase()}</Text>
              </View>
              <View style={styles.cardInfo}>
                <View style={styles.nameRow}>
                  <Text variant="titleMedium" style={styles.userName} numberOfLines={1}>
                    {item.fullName}
                  </Text>
                  {item.isOwner ? (
                    <Chip style={styles.ownerChip} textStyle={styles.ownerChipText}>
                      {t('user.owner')}
                    </Chip>
                  ) : null}
                </View>
                {item.email ? <Text variant="bodySmall" style={styles.subText}>{item.email}</Text> : null}
                {item.phone ? <Text variant="bodySmall" style={styles.subText}>{item.phone}</Text> : null}
                <View style={styles.chipRow}>
                  {primaryAssignment ? (
                    <Chip style={styles.roleChip} textStyle={styles.chipText}>
                      {primaryAssignment.roleName}
                    </Chip>
                  ) : null}
                  {primaryAssignment ? (
                    <Chip style={styles.orgChip} textStyle={styles.chipText}>
                      {primaryAssignment.orgUnitName}
                    </Chip>
                  ) : null}
                  {item.linkedWorkerId ? (
                    <Chip icon="account-hard-hat" style={styles.workerChip} textStyle={styles.chipText}>
                      {item.linkedWorkerName || t('user.hasWorker')}
                    </Chip>
                  ) : null}
                </View>
              </View>
              <View style={styles.cardActions}>
                <View style={[styles.statusDot, {backgroundColor: item.isActive ? colors.success : colors.textLight}]} />
                <IconButton
                  icon="pencil-outline"
                  size={20}
                  iconColor={colors.textSecondary}
                  onPress={() => navigation.navigate('UserForm', {userId: item.id})}
                  style={styles.actionBtn}
                />
                {!item.isOwner && (
                  item.isActive ? (
                    <IconButton
                      icon="account-off-outline"
                      size={20}
                      iconColor={colors.error}
                      onPress={() => { setActionUser(item); setDeactivateVisible(true); }}
                      style={styles.actionBtn}
                    />
                  ) : (
                    <IconButton
                      icon="account-check-outline"
                      size={20}
                      iconColor={colors.success}
                      onPress={() => handleReactivate(item)}
                      style={styles.actionBtn}
                    />
                  )
                )}
              </View>
            </View>
          </Card.Content>
        </Card>
      </TouchableOpacity>
    );
  }, [navigation, t, handleReactivate]);

  if (loading && users.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader
        title={t('user.title')}
        onBack={() => navigation.goBack()}
      />

      {/* Search + Filter Bar */}
      <View style={styles.searchRow}>
        <Searchbar
          placeholder={t('user.searchPlaceholder')}
          value={searchQuery}
          onChangeText={handleSearch}
          onSubmitEditing={handleSearchSubmit}
          style={styles.searchbar}
          inputStyle={styles.searchInput}
        />
        <IconButton
          icon={hasFilters ? 'filter-check' : 'filter-variant'}
          size={24}
          iconColor={hasFilters ? colors.primary : colors.textSecondary}
          onPress={() => setFilterVisible(true)}
        />
      </View>

      {/* Active filter chips */}
      {hasFilters && (
        <View style={styles.activeFilters}>
          {filterOrgUnitId && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterOrgUnitId(null)}>
              {orgUnitMap[filterOrgUnitId] || t('user.store')}
            </Chip>
          )}
          {filterRoleId && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterRoleId(null)}>
              {roleMap[filterRoleId] || t('user.role')}
            </Chip>
          )}
          {filterStatus !== null && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterStatus(null)}>
              {filterStatus ? t('user.active') : t('user.inactive')}
            </Chip>
          )}
          {filterHasWorker !== null && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterHasWorker(null)}>
              {filterHasWorker ? t('user.hasWorker') : t('user.noWorker')}
            </Chip>
          )}
          <TouchableOpacity onPress={clearFilters}>
            <Text style={styles.clearFilters}>{t('user.clearFilters')}</Text>
          </TouchableOpacity>
        </View>
      )}

      <FlatList
        data={users}
        renderItem={renderUser}
        keyExtractor={item => String(item.id)}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text variant="headlineSmall" style={styles.emptyIcon}>&#128100;</Text>
            <Text variant="titleMedium" style={styles.emptyText}>{t('user.noUsers')}</Text>
            <Text variant="bodyMedium" style={styles.emptySubtext}>{t('user.noUsersHint')}</Text>
            <Button
              mode="contained"
              icon="plus"
              onPress={() => navigation.navigate('UserForm')}
              style={styles.emptyButton}
              buttonColor={colors.primary}>
              {t('user.addUser')}
            </Button>
          </View>
        }
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }
      />

      <FAB icon="plus" style={styles.fab} onPress={() => navigation.navigate('UserForm')} color={colors.white} />

      {/* Filter Dialog */}
      <Portal>
        <Dialog visible={filterVisible} onDismiss={() => setFilterVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('user.filters')}</Dialog.Title>
          <Dialog.ScrollArea style={{maxHeight: 420, paddingHorizontal: 0}}>
            <ScrollView>
              <View style={styles.filterSection}>
                <Text variant="labelLarge" style={styles.filterLabel}>{t('user.store')}</Text>
                <View style={styles.filterListBox}>
                  <ScrollView nestedScrollEnabled>
                    {orgUnits.map(ou => {
                      const selected = filterOrgUnitId === ou.id;
                      return (
                        <TouchableOpacity
                          key={ou.id}
                          style={[styles.filterListItem, selected && styles.filterListItemSelected]}
                          onPress={() => setFilterOrgUnitId(selected ? null : ou.id)}
                          activeOpacity={0.7}>
                          <Text style={[styles.filterListItemText, selected && styles.filterListItemTextSelected]}>
                            {ou.name}
                          </Text>
                          {selected && <IconButton icon="check" size={16} iconColor={colors.primary} style={{margin: 0, padding: 0, width: 20, height: 20}} />}
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                </View>

                <Text variant="labelLarge" style={styles.filterLabel}>{t('user.role')}</Text>
                <View style={styles.filterListBox}>
                  <ScrollView nestedScrollEnabled>
                    {roles.map(r => {
                      const selected = filterRoleId === r.id;
                      return (
                        <TouchableOpacity
                          key={r.id}
                          style={[styles.filterListItem, selected && styles.filterListItemSelected]}
                          onPress={() => setFilterRoleId(selected ? null : r.id)}
                          activeOpacity={0.7}>
                          <Text style={[styles.filterListItemText, selected && styles.filterListItemTextSelected]}>
                            {r.name}
                          </Text>
                          {selected && <IconButton icon="check" size={16} iconColor={colors.primary} style={{margin: 0, padding: 0, width: 20, height: 20}} />}
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                </View>

                <Text variant="labelLarge" style={styles.filterLabel}>{t('user.status')}</Text>
                <View style={styles.filterChipGroup}>
                  <Chip selected={filterStatus === true}
                    onPress={() => setFilterStatus(filterStatus === true ? null : true)}
                    style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                    {t('user.active')}
                  </Chip>
                  <Chip selected={filterStatus === false}
                    onPress={() => setFilterStatus(filterStatus === false ? null : false)}
                    style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                    {t('user.inactive')}
                  </Chip>
                </View>

                <Text variant="labelLarge" style={styles.filterLabel}>{t('user.workerLink')}</Text>
                <View style={styles.filterChipGroup}>
                  <Chip selected={filterHasWorker === true}
                    onPress={() => setFilterHasWorker(filterHasWorker === true ? null : true)}
                    style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                    {t('user.hasWorker')}
                  </Chip>
                  <Chip selected={filterHasWorker === false}
                    onPress={() => setFilterHasWorker(filterHasWorker === false ? null : false)}
                    style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                    {t('user.noWorker')}
                  </Chip>
                </View>
              </View>
            </ScrollView>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={clearFilters}>{t('user.clearFilters')}</Button>
            <Button mode="contained" onPress={() => { setFilterVisible(false); handleSearchSubmit(); }}
              style={{borderRadius: 10}} buttonColor={colors.primary}>
              {t('user.applyFilters')}
            </Button>
          </Dialog.Actions>
        </Dialog>

        {/* Deactivate Confirmation */}
        <Dialog visible={deactivateVisible} onDismiss={() => setDeactivateVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('user.deactivateTitle')}</Dialog.Title>
          <Dialog.Content>
            <Text>{t('user.deactivateMessage', {name: actionUser?.fullName})}</Text>
            <Text variant="bodySmall" style={styles.deactivateHint}>{t('user.deactivateHint')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeactivateVisible(false)}>{t('common.cancel')}</Button>
            <Button textColor={colors.error} onPress={handleDeactivate}>{t('user.deactivate')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  searchRow: {flexDirection: 'row', alignItems: 'center', paddingHorizontal: spacing.sm, paddingTop: spacing.sm},
  searchbar: {flex: 1, backgroundColor: colors.surface, elevation: 1, height: 44},
  searchInput: {fontSize: 14, minHeight: 44},
  activeFilters: {
    flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center',
    paddingHorizontal: spacing.md, paddingTop: spacing.xs, gap: spacing.xs,
  },
  filterChip: {backgroundColor: colors.primary + '20', height: 30},
  filterChipText: {fontSize: 12},
  clearFilters: {color: colors.primary, fontSize: 12, fontWeight: '600', marginLeft: spacing.xs},
  listContent: {padding: spacing.md, paddingBottom: 80},
  card: {marginBottom: spacing.sm, backgroundColor: colors.surface, borderRadius: 8},
  cardInactive: {opacity: 0.55},
  cardContent: {paddingVertical: spacing.sm},
  cardHeader: {flexDirection: 'row', alignItems: 'flex-start'},
  avatar: {
    width: 44, height: 44, borderRadius: 22, backgroundColor: colors.secondary,
    justifyContent: 'center', alignItems: 'center', marginRight: spacing.md,
  },
  avatarText: {color: colors.white, fontSize: 18, fontWeight: 'bold'},
  cardInfo: {flex: 1},
  nameRow: {flexDirection: 'row', alignItems: 'center', gap: spacing.sm},
  userName: {fontWeight: '600', color: colors.text, flex: 1},
  subText: {color: colors.textSecondary, marginTop: 2},
  chipRow: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs},
  roleChip: {height: 24, paddingVertical: 0, backgroundColor: colors.primary + '15'},
  orgChip: {height: 24, paddingVertical: 0, backgroundColor: colors.secondary + '15'},
  workerChip: {height: 24, paddingVertical: 0, backgroundColor: colors.success + '15'},
  ownerChip: {height: 22, paddingVertical: 0, backgroundColor: colors.warning + '20'},
  ownerChipText: {fontSize: 10, fontWeight: '700'},
  chipText: {fontSize: 11, marginTop: 0},
  cardActions: {alignItems: 'center', marginLeft: spacing.xs},
  statusDot: {width: 10, height: 10, borderRadius: 5, marginBottom: 4},
  actionBtn: {margin: 0, padding: 0, width: 32, height: 32},
  emptyContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: spacing.xxl * 2},
  emptyIcon: {fontSize: 48, marginBottom: spacing.md},
  emptyText: {color: colors.text, fontWeight: '600'},
  emptySubtext: {color: colors.textSecondary, marginTop: spacing.xs, textAlign: 'center', paddingHorizontal: spacing.xl},
  emptyButton: {marginTop: spacing.lg, borderRadius: 8},
  fab: {position: 'absolute', right: spacing.md, bottom: spacing.md, backgroundColor: colors.primary},
  filterSection: {paddingHorizontal: spacing.lg, paddingVertical: spacing.sm},
  filterLabel: {fontWeight: '600', color: colors.text, marginTop: spacing.md, marginBottom: spacing.xs},
  filterChipGroup: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs},
  filterSelectChip: {backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border},
  filterListBox: {
    borderWidth: 1, borderColor: colors.border, borderRadius: 8,
    backgroundColor: colors.surface, maxHeight: 75, overflow: 'hidden',
  },
  filterListItem: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 5, paddingHorizontal: 12,
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.divider,
  },
  filterListItemSelected: {backgroundColor: colors.primary + '12'},
  filterListItemText: {fontSize: 14, color: colors.text},
  filterListItemTextSelected: {fontWeight: '600', color: colors.primary},
  deactivateHint: {color: colors.textSecondary, marginTop: spacing.sm, fontStyle: 'italic'},
});

export default UserListScreen;
